import { createContext, type HTMLProps, type RefCallback, use } from 'react'

/** The props floating-ui's prop getters take and return for a menu's trigger, panel and items. */
export type DropdownHtmlProps = Omit<HTMLProps<HTMLElement>, 'ref'> & {
    ref?: RefCallback<HTMLElement>
}

/**
 * Which of a menu's submenus is open. A menu owns this rather than each submenu owning its own flag,
 * because the thing that decides a submenu should close is always something that happened elsewhere in
 * the menu around it — a sibling opening, the pointer moving onto another row — and only the menu sees
 * all of it. Two panels can never both believe they are open.
 */
export type OpenSubmenu = {
    id: string | null
    /**
     * The panel took over from a sibling rather than appearing on a resting menu, which looks like the
     * pointer walking down a column of triggers. Animating each one in turn reads as flicker, so a
     * hand-off is instant.
     */
    handoff: boolean
}

export const NO_SUBMENU: OpenSubmenu = { id: null, handoff: false }

type DropdownMenuContextValue = {
    getItemProps: (userProps: DropdownHtmlProps) => DropdownHtmlProps
    /** Closes this menu and every menu it opened out of, which is what choosing an item has to do. */
    closeMenu: () => void
    /**
     * Whether this menu is skipping its animation, so a submenu opened from it makes the same choice.
     * A menu told to hold still that spawns an animating panel is worse than either alone.
     */
    skipMotion: boolean
    /**
     * The row a searching panel's Enter would choose. It is null in a menu without a search field, where
     * the row in play is the focused one and the browser marks it for us.
     */
    activeIndex: number | null
    /** Prefix for item ids, so a search field can point `aria-activedescendant` at the active row. */
    itemIdPrefix?: string
    /**
     * Returns the caret to a searching panel's field after a row is chosen: a checkbox list is worked
     * through by typing, choosing, and typing again.
     */
    focusSearch?: () => void
    /** Opens one of this menu's submenus, replacing whichever was open, or closes them all with `null`. */
    setOpenSubmenu: (nodeId: string | null) => void
}

export const DropdownMenuContext = createContext<DropdownMenuContextValue | null>(null)

/**
 * Held apart from the rest of the menu context so that opening a submenu re-renders the submenus, which
 * have to react to it, and not the menu's items, which do not.
 */
export const OpenSubmenuContext = createContext<OpenSubmenu>(NO_SUBMENU)

export function useDropdownMenuContext() {
    return use(DropdownMenuContext)
}

export function useOpenSubmenu() {
    return use(OpenSubmenuContext)
}
